import React from 'react';
import { useLandingPageData } from '../../context/LandingPageContext';
import { useTheme } from '../../context/ThemeContext';
import { useLanguage } from '../../context/LanguageContext';
import { Check, X, Minus, Gem, PenTool, Combine, ArrowRight, Info } from 'lucide-react';

const comparisonData = {
  de: {
    sectionTitle: 'FUE vs. DHI vs. Saphir DHI Hybrid',
    sectionSubtitle: 'Warum die Saphir DHI Hybridmethode das Beste aus beiden Welten vereint',
    methods: [
      { key: 'fue', name: 'Standard FUE', icon: PenTool },
      { key: 'dhi', name: 'DHI Methode', icon: Gem },
      { key: 'hybrid', name: 'Saphir DHI Hybrid', icon: Combine },
    ],
    categories: [
      {
        label: 'Ohne Rasur möglich',
        fue: { text: 'Nein, Rasur erforderlich', score: 'bad' },
        dhi: { text: 'Ja, komplett ohne Rasur', score: 'best' },
        hybrid: { text: 'Ja, komplett ohne Rasur', score: 'best' },
      },
      {
        label: 'Kanalöffnung',
        fue: { text: 'Stahl-Klingen', score: 'mid' },
        dhi: { text: 'Choi-Pen (direkt)', score: 'best' },
        hybrid: { text: 'Saphir-Klingen (feiner)', score: 'best' },
      },
      {
        label: 'Implantation',
        fue: { text: 'Manuell in Kanäle', score: 'mid' },
        dhi: { text: 'Direkt mit Choi-Pen', score: 'best' },
        hybrid: { text: 'Direkt mit Choi-Pen', score: 'best' },
      },
      {
        label: 'Heilungszeit',
        fue: { text: '10–14 Tage sichtbar', score: 'bad' },
        dhi: { text: '5–7 Tage', score: 'best' },
        hybrid: { text: '3–5 Tage (Saphir heilt schneller)', score: 'best' },
      },
      {
        label: 'Natürliche Wuchsrichtung',
        fue: { text: 'Gut', score: 'mid' },
        dhi: { text: 'Sehr präzise steuerbar', score: 'best' },
        hybrid: { text: 'Maximale Präzision', score: 'best' },
      },
      {
        label: 'Haardichte möglich',
        fue: { text: '40–50 Grafts/cm²', score: 'mid' },
        dhi: { text: '60–80 Grafts/cm²', score: 'best' },
        hybrid: { text: '80–100 Grafts/cm² (dichteste)', score: 'best' },
      },
      {
        label: 'Diskretion',
        fue: { text: 'Rasur sichtbar', score: 'bad' },
        dhi: { text: 'Sofort unauffällig', score: 'best' },
        hybrid: { text: 'Sofort unauffällig', score: 'best' },
      },
    ],
    honestNotes: [
      {
        label: 'Behandlungsdauer',
        fue: { text: '4–6 Stunden', score: 'best' },
        dhi: { text: '6–8 Stunden', score: 'mid' },
        hybrid: { text: '6–8 Stunden', score: 'mid' },
      },
      {
        label: 'Kosten',
        fue: { text: 'Am günstigsten', score: 'best' },
        dhi: { text: 'Höher', score: 'mid' },
        hybrid: { text: 'Premium-Investition', score: 'mid' },
      },
      {
        label: 'Max. Grafts/Sitzung',
        fue: { text: 'Bis 5.000+', score: 'best' },
        dhi: { text: 'Bis 3.000', score: 'mid' },
        hybrid: { text: 'Bis 3.500', score: 'mid' },
      },
    ],
    honestNotesTitle: 'Gut zu wissen',
    conclusion: 'Saphir DHI Hybrid — maximale Dichte, schnellste Heilung, ohne Rasur',
    ctaText: 'Kostenlose Beratung vereinbaren',
  },
  en: {
    sectionTitle: 'FUE vs. DHI vs. Sapphire DHI Hybrid',
    sectionSubtitle: 'Why the Sapphire DHI Hybrid method combines the best of both worlds',
    methods: [
      { key: 'fue', name: 'Standard FUE', icon: PenTool },
      { key: 'dhi', name: 'DHI Method', icon: Gem },
      { key: 'hybrid', name: 'Sapphire DHI Hybrid', icon: Combine },
    ],
    categories: [
      {
        label: 'No shaving needed',
        fue: { text: 'No, shaving required', score: 'bad' },
        dhi: { text: 'Yes, completely unshaved', score: 'best' },
        hybrid: { text: 'Yes, completely unshaved', score: 'best' },
      },
      {
        label: 'Channel opening',
        fue: { text: 'Steel blades', score: 'mid' },
        dhi: { text: 'Choi-Pen (direct)', score: 'best' },
        hybrid: { text: 'Sapphire blades (finer)', score: 'best' },
      },
      {
        label: 'Implantation',
        fue: { text: 'Manual into channels', score: 'mid' },
        dhi: { text: 'Direct with Choi-Pen', score: 'best' },
        hybrid: { text: 'Direct with Choi-Pen', score: 'best' },
      },
      {
        label: 'Healing time',
        fue: { text: '10–14 days visible', score: 'bad' },
        dhi: { text: '5–7 days', score: 'best' },
        hybrid: { text: '3–5 days (sapphire heals faster)', score: 'best' },
      },
      {
        label: 'Natural growth direction',
        fue: { text: 'Good', score: 'mid' },
        dhi: { text: 'Very precise control', score: 'best' },
        hybrid: { text: 'Maximum precision', score: 'best' },
      },
      {
        label: 'Hair density possible',
        fue: { text: '40–50 grafts/cm²', score: 'mid' },
        dhi: { text: '60–80 grafts/cm²', score: 'best' },
        hybrid: { text: '80–100 grafts/cm² (densest)', score: 'best' },
      },
      {
        label: 'Discretion',
        fue: { text: 'Shaving visible', score: 'bad' },
        dhi: { text: 'Immediately inconspicuous', score: 'best' },
        hybrid: { text: 'Immediately inconspicuous', score: 'best' },
      },
    ],
    honestNotes: [
      {
        label: 'Treatment duration',
        fue: { text: '4–6 hours', score: 'best' },
        dhi: { text: '6–8 hours', score: 'mid' },
        hybrid: { text: '6–8 hours', score: 'mid' },
      },
      {
        label: 'Cost',
        fue: { text: 'Most affordable', score: 'best' },
        dhi: { text: 'Higher', score: 'mid' },
        hybrid: { text: 'Premium investment', score: 'mid' },
      },
      {
        label: 'Max grafts/session',
        fue: { text: 'Up to 5,000+', score: 'best' },
        dhi: { text: 'Up to 3,000', score: 'mid' },
        hybrid: { text: 'Up to 3,500', score: 'mid' },
      },
    ],
    honestNotesTitle: 'Good to know',
    conclusion: 'Sapphire DHI Hybrid — maximum density, fastest healing, no shaving',
    ctaText: 'Book free consultation',
  },
};

const ScoreIcon = ({ score }) => {
  if (score === 'best') return <Check className="w-5 h-5 text-emerald-500" />;
  if (score === 'mid') return <Minus className="w-5 h-5 text-amber-500" />;
  return <X className="w-5 h-5 text-red-400" />;
};

const DhiMethodComparison = () => {
  const { features } = useLandingPageData();
  const { isDark } = useTheme();
  const { language } = useLanguage();

  if (!features?.showDhiComparison) return null;

  const t = comparisonData[language] || comparisonData.de;
  const methodKeys = ['fue', 'dhi', 'hybrid'];

  return (
    <section
      className={`py-20 md:py-28 relative overflow-hidden transition-colors duration-500 ${
        isDark ? 'bg-[#0a0a0a]' : 'bg-gradient-to-b from-gray-50 to-white'
      }`}
      data-testid="dhi-method-comparison-section"
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-14">
          <h2 className={`text-2xl sm:text-3xl lg:text-4xl font-bold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
            {t.sectionTitle}
          </h2>
          <p className={`text-base lg:text-lg max-w-2xl mx-auto ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
            {t.sectionSubtitle}
          </p>
        </div>

        {/* Desktop Table */}
        <div className="hidden md:block">
          <div className={`rounded-2xl overflow-hidden border ${isDark ? 'border-white/10 bg-white/[0.02]' : 'border-gray-200 bg-white'}`}>
            {/* Header */}
            <div className={`grid grid-cols-4 ${isDark ? 'bg-white/[0.04]' : 'bg-gray-50'}`}>
              <div className={`p-5 font-semibold text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                {language === 'de' ? 'Kriterium' : 'Criteria'}
              </div>
              {t.methods.map((method) => {
                const Icon = method.icon;
                const isHybrid = method.key === 'hybrid';
                return (
                  <div key={method.key} className={`p-5 text-center font-semibold text-sm ${
                    isHybrid
                      ? isDark ? 'text-[#7BA7C2] bg-[#7BA7C2]/5' : 'text-[#5A8BA6] bg-[#5A8BA6]/5'
                      : isDark ? 'text-gray-300' : 'text-gray-700'
                  }`}>
                    <div className="flex items-center justify-center gap-2">
                      <Icon className="w-4 h-4" />
                      {method.name}
                    </div>
                  </div>
                );
              })}
            </div>
            {/* Rows */}
            {t.categories.map((cat, idx) => (
              <div key={cat.label} className={`grid grid-cols-4 transition-colors ${
                isDark
                  ? `border-t border-white/5 ${idx % 2 !== 0 ? 'bg-white/[0.01]' : ''}`
                  : `border-t border-gray-100 ${idx % 2 !== 0 ? 'bg-gray-50/50' : ''}`
              }`}>
                <div className={`p-5 font-medium text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{cat.label}</div>
                {methodKeys.map((mk) => {
                  const cell = cat[mk];
                  const isHybrid = mk === 'hybrid';
                  return (
                    <div key={mk} className={`p-5 text-center text-sm ${isHybrid ? isDark ? 'bg-[#7BA7C2]/5' : 'bg-[#5A8BA6]/5' : ''}`}>
                      <div className="flex items-center justify-center gap-2">
                        <ScoreIcon score={cell.score} />
                        <span className={cell.score === 'best' ? isDark ? 'text-white font-medium' : 'text-gray-900 font-medium' : isDark ? 'text-gray-400' : 'text-gray-500'}>
                          {cell.text}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            ))}
            {/* Honest Notes Separator */}
            <div className={`grid grid-cols-4 ${isDark ? 'bg-white/[0.04] border-t border-white/10' : 'bg-amber-50/60 border-t-2 border-amber-200/50'}`}>
              <div className={`p-4 col-span-4 flex items-center gap-2 ${isDark ? 'text-amber-400' : 'text-amber-700'}`}>
                <Info className="w-4 h-4" />
                <span className="text-sm font-semibold">{t.honestNotesTitle}</span>
                <span className={`text-xs ${isDark ? 'text-amber-400/60' : 'text-amber-600/70'}`}>
                  — {language === 'de' ? 'Wir sind transparent' : 'We believe in transparency'}
                </span>
              </div>
            </div>
            {t.honestNotes.map((cat, idx) => (
              <div key={cat.label} className={`grid grid-cols-4 transition-colors ${
                isDark
                  ? `border-t border-white/5 ${idx % 2 === 0 ? 'bg-amber-900/[0.03]' : 'bg-amber-900/[0.06]'}`
                  : `border-t border-amber-100/50 ${idx % 2 === 0 ? 'bg-amber-50/20' : 'bg-amber-50/40'}`
              }`}>
                <div className={`p-5 font-medium text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>{cat.label}</div>
                {methodKeys.map((mk) => {
                  const cell = cat[mk];
                  const isHybrid = mk === 'hybrid';
                  return (
                    <div key={mk} className={`p-5 text-center text-sm ${isHybrid ? isDark ? 'bg-[#7BA7C2]/5' : 'bg-[#5A8BA6]/[0.03]' : ''}`}>
                      <div className="flex items-center justify-center gap-2">
                        <ScoreIcon score={cell.score} />
                        <span className={isDark ? 'text-gray-400' : 'text-gray-500'}>{cell.text}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>

        {/* Mobile Compact Table */}
        <div className="md:hidden">
          <div className={`rounded-2xl overflow-hidden border ${isDark ? 'border-white/10 bg-white/[0.02]' : 'border-gray-200 bg-white'}`}>
            <div className={`grid grid-cols-[1fr_52px_52px_52px] ${isDark ? 'bg-white/[0.04]' : 'bg-gray-50'}`}>
              <div className={`px-3 py-3`} />
              {t.methods.map((method) => {
                const Icon = method.icon;
                const isHybrid = method.key === 'hybrid';
                return (
                  <div key={method.key} className={`px-1 py-3 text-center ${isHybrid ? isDark ? 'bg-[#7BA7C2]/8' : 'bg-[#5A8BA6]/5' : ''}`}>
                    <Icon className={`w-3.5 h-3.5 mx-auto mb-0.5 ${isHybrid ? isDark ? 'text-[#7BA7C2]' : 'text-[#5A8BA6]' : isDark ? 'text-gray-400' : 'text-gray-500'}`} />
                    <span className={`text-[9px] font-semibold leading-tight block ${isHybrid ? isDark ? 'text-[#7BA7C2]' : 'text-[#5A8BA6]' : isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                      {method.key === 'fue' ? 'FUE' : method.key === 'dhi' ? 'DHI' : 'Hybrid'}
                    </span>
                  </div>
                );
              })}
            </div>
            {t.categories.map((cat, idx) => (
              <div key={cat.label} className={`grid grid-cols-[1fr_52px_52px_52px] items-center ${
                isDark ? `border-t border-white/5 ${idx % 2 !== 0 ? 'bg-white/[0.01]' : ''}` : `border-t border-gray-100 ${idx % 2 !== 0 ? 'bg-gray-50/50' : ''}`
              }`}>
                <div className={`px-3 py-2.5 text-xs font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  {cat.label}
                  <span className={`block text-[10px] font-normal mt-0.5 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{cat.hybrid.text}</span>
                </div>
                {methodKeys.map((mk) => {
                  const cell = cat[mk];
                  const isHybrid = mk === 'hybrid';
                  return (
                    <div key={mk} className={`py-2.5 flex justify-center ${isHybrid ? isDark ? 'bg-[#7BA7C2]/5' : 'bg-[#5A8BA6]/[0.03]' : ''}`}>
                      <ScoreIcon score={cell.score} />
                    </div>
                  );
                })}
              </div>
            ))}
            <div className={`grid grid-cols-1 ${isDark ? 'bg-white/[0.04] border-t border-white/10' : 'bg-amber-50/60 border-t-2 border-amber-200/50'}`}>
              <div className={`px-3 py-2.5 flex items-center gap-1.5 ${isDark ? 'text-amber-400' : 'text-amber-700'}`}>
                <Info className="w-3.5 h-3.5" />
                <span className="text-xs font-semibold">{t.honestNotesTitle}</span>
              </div>
            </div>
            {t.honestNotes.map((cat, idx) => (
              <div key={cat.label} className={`grid grid-cols-[1fr_52px_52px_52px] items-center ${
                isDark ? `border-t border-white/5 ${idx % 2 === 0 ? 'bg-amber-900/[0.03]' : 'bg-amber-900/[0.06]'}` : `border-t border-amber-100/50 ${idx % 2 === 0 ? 'bg-amber-50/20' : 'bg-amber-50/40'}`
              }`}>
                <div className={`px-3 py-2.5 text-xs ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  {cat.label}
                  <span className={`block text-[10px] font-normal mt-0.5 ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>{cat.hybrid.text}</span>
                </div>
                {methodKeys.map((mk) => {
                  const cell = cat[mk];
                  const isHybrid = mk === 'hybrid';
                  return (
                    <div key={mk} className={`py-2.5 flex justify-center ${isHybrid ? isDark ? 'bg-[#7BA7C2]/5' : 'bg-[#5A8BA6]/[0.03]' : ''}`}>
                      <ScoreIcon score={cell.score} />
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>

        {/* Conclusion */}
        <div className="mt-12 text-center">
          <p className={`text-lg md:text-xl font-semibold mb-6 ${isDark ? 'text-white' : 'text-gray-900'}`}>
            {t.conclusion}
          </p>
          <button
            onClick={() => { const el = document.querySelector('#kontakt'); if (el) el.scrollIntoView({ behavior: 'smooth' }); }}
            className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full text-white font-medium transition-all duration-300 hover:scale-105 bg-[#7BA7C2] hover:bg-[#6b97b2] shadow-lg hover:shadow-xl"
            data-testid="dhi-comparison-cta-btn"
          >
            {t.ctaText}
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </section>
  );
};

export default DhiMethodComparison;
